import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  type KeyboardEvent,
} from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { PATTERN_MARK_CONFIDENCE, type PatternCard, type PatternMarkValue } from "@shared/schema";
import { Archive, RefreshCw, MessageCircle, PenLine } from "lucide-react";
import { useLocation } from "wouter";
import { usePullToRefresh } from "@/hooks/use-pull-to-refresh";
import { MirrorCard } from "@/components/MirrorCard";
import { AtmosphericPage } from "@/components/MirrorAtmosphere";
import { formatTypographicQuotes } from "@/lib/typographic-quotes";
import {
  PATTERN_LENSES,
  getPatternLens,
  type PatternLens,
} from "@shared/pattern-lenses";

type PatternMark = {
  id: number;
  mark: PatternMarkValue;
  markedAt: string | Date;
  baselineCount?: number | null;
  resolvedAt?: string | Date | null;
  absenceAcknowledgedAt?: string | Date | null;
};

type PatternRecurrence = {
  sinceCount?: number | null;
  priorCount?: number | null;
  lastAppearedAt?: string | Date | null;
  daysSinceLast?: number | null;
};

type EnrichedPatternCard = PatternCard & {
  patternMark?: PatternMark | null;
  recurrence?: PatternRecurrence | null;
};

function BoldText({ text }: { text: string }) {
  if (!text || typeof text !== "string") return <>{""}</>;
  try {
    const formattedText = formatTypographicQuotes(text);
    const parts = formattedText.split(/(\d+%|\d+x|\d+\/\d+|\b\d+\s*(?:days?|hours?|times?|entries|points?)\b)/gi);
    return (
      <>
        {parts.map((part, i) => {
          if (/^\d+%$|^\d+x$|^\d+\/\d+$|^\d+\s*(?:days?|hours?|times?|entries|points?)$/i.test(part)) {
            return <span key={i} className="font-semibold" style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)" }}>{part}</span>;
          }
          return <span key={i}>{part}</span>;
        })}
      </>
    );
  } catch {
    return <>{formatTypographicQuotes(text)}</>;
  }
}

function safeString(val: unknown): string {
  if (typeof val === "string") return val;
  if (val == null) return "";
  return String(val);
}

function safeArray(val: unknown): string[] {
  if (Array.isArray(val)) return val.map(v => safeString(v));
  return [];
}

function safeDate(val: unknown, timeZone?: string): string {
  try {
    if (!val) return "";
    const d = new Date(val as string | number);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", timeZone });
  } catch {
    return "";
  }
}

function ConfidenceLabel({ value }: { value: number }) {
  const safeValue = typeof value === "number" && !isNaN(value) ? value : 0;
  if (safeValue >= 75) {
    return (
      <span className="type-label" style={{ color: "var(--accent-color)" }}>
        Pattern
      </span>
    );
  }
  if (safeValue >= 50) {
    return (
      <span className="type-label" style={{ color: "var(--accent-text-soft)" }}>
        Observation
      </span>
    );
  }
  return (
    <span className="type-label" style={{ color: "var(--text-muted)" }}>
      Early signal
    </span>
  );
}

function ActionChips() {
  const [, setLocation] = useLocation();
  return (
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => setLocation("/chat")}
        className="touch-target flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
        style={{
          background: "var(--accent-bg)",
          border: "1px solid var(--accent-border)",
          color: "var(--accent-color)",
          fontFamily: "var(--font-display)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
        data-testid="chip-talk-mirror"
      >
        <MessageCircle size={11} />
        Talk to Fractal about this
      </button>
      <button
        onClick={() => setLocation("/log")}
        className="touch-target flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all hover:opacity-80"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border-light)",
          color: "var(--text-muted)",
          fontFamily: "var(--font-display)",
          fontSize: "0.75rem",
          fontWeight: 500,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
        data-testid="chip-write-about"
      >
        <PenLine size={11} />
        Log a related entry
      </button>
    </div>
  );
}


function PatternCardComponent({ card, onArchive, isArchiving, onMark, onResolve, isMarking, timeZone, index }: {
  card: EnrichedPatternCard;
  onArchive: (id: number) => void;
  isArchiving: boolean;
  onMark: (id: number, mark: PatternMark["mark"] | null) => void;
  onResolve: (id: number, resolved: boolean) => void;
  isMarking: boolean;
  timeZone?: string;
  index: number;
}) {
  const [expanded, setExpanded] = useState(false);

  const hasDetail = Boolean(
    safeString(card.narrative)
    || safeArray(card.dataPoints).length > 0
    || safeArray(card.contributingFactors).length > 0
    || safeString(card.recommendation),
  );
  const mark = card.patternMark;
  const recurrence = card.recurrence;
  const canMark = Number(card.confidence) >= PATTERN_MARK_CONFIDENCE;
  const recurrenceCount = Number(mark?.baselineCount ?? recurrence?.sinceCount ?? 0);
  const sinceCount = Number(recurrence?.sinceCount ?? 0);
  const priorCount = Number(recurrence?.priorCount ?? 0);
  const canResolve = mark?.mark === "change"
    && !mark.resolvedAt
    && !mark.absenceAcknowledgedAt
    && Number(recurrence?.daysSinceLast ?? 0) >= 30;
  const changeComparison = sinceCount > priorCount
    ? `up from ${priorCount}`
    : sinceCount < priorCount
      ? `down from ${priorCount}`
      : `the same as ${priorCount}`;

  return (
    <MirrorCard
      className="fade-in"
      testId={`pattern-card-${card.id}`}
    >
      <div style={{ animationDelay: `${index * 0.08}s` }}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3
              className="leading-tight"
              style={{
                color: "var(--fg-raw)",
                fontFamily: "var(--font-reflective)",
                fontSize: "1.375rem",
                fontWeight: 400,
              }}
              data-testid={`text-headline-${card.id}`}
            >
              {formatTypographicQuotes(card.headline)}
            </h3>
            <p
              className="mt-2 leading-relaxed"
              style={{
                color: "var(--text-muted)",
                fontFamily: "var(--font-reflective)",
                fontSize: "1rem",
                fontWeight: 400,
              }}
              data-testid={`text-insight-${card.id}`}
            >
              {formatTypographicQuotes(card.insight)}
            </p>
          </div>
          <button
            onClick={() => onArchive(card.id)}
            disabled={isArchiving}
            aria-label="Archive pattern"
            data-testid={`button-archive-${card.id}`}
            className="touch-target opacity-60 hover:opacity-100 transition-opacity disabled:opacity-60 p-1 rounded"
          >
            <Archive size={12} style={{ color: "var(--fg-raw)" }} />
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap mt-4">
          <ConfidenceLabel value={card.confidence} />
          <span className="type-label" style={{ color: "var(--text-muted)" }}>
            {Math.round(Number(card.confidence) || 0)}%
          </span>
          {safeString(card.timespan) && (
            <>
              <span className="type-label" style={{ color: "var(--border-medium)" }}>·</span>
              <span
                className="type-label"
                style={{ color: "var(--text-muted)" }}
              >
                {formatTypographicQuotes(safeString(card.timespan))}
              </span>
            </>
          )}
        </div>

        {canMark ? (
          <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-hairline)" }} data-testid={`pattern-mark-controls-${card.id}`}>
            <div className="mb-3 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <span
                style={{ color: "var(--fg-raw)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em" }}
              >
                MARK THIS PATTERN
              </span>
              <span
                style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", letterSpacing: "0.04em" }}
              >
                Recurs {recurrenceCount} {recurrenceCount === 1 ? "time" : "times"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([
                ["change", "Change it", "Look closer"],
                ["sit", "Sit with it", "Leave it open"],
              ] as const).map(([value, label, sublabel]) => {
                const selected = mark?.mark === value && !mark.resolvedAt;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isMarking}
                    onClick={() => onMark(card.id, selected ? null : value)}
                    aria-pressed={selected}
                    className="touch-target px-4 py-3 text-left transition-colors disabled:opacity-50"
                    style={{
                      color: selected ? "var(--accent-color)" : "var(--text-primary)",
                      border: selected ? "1px solid var(--accent-color)" : "1px solid var(--border-medium)",
                      borderRadius: "18px",
                      background: selected ? "var(--accent-bg)" : "var(--surface-1)",
                    }}
                    data-testid={`button-mark-${value}-${card.id}`}
                  >
                    <span className="flex items-center justify-between gap-3">
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          letterSpacing: "0.04em",
                        }}
                      >
                        {label}
                      </span>
                      <span className="flex items-center gap-2">
                        <span
                          style={{
                            color: selected ? "var(--accent-color)" : "var(--text-muted)",
                            fontFamily: "var(--font-mono)",
                            fontSize: "0.625rem",
                            fontWeight: 600,
                            letterSpacing: "0.08em",
                          }}
                        >
                          {selected ? "MARKED" : "CHOOSE"}
                        </span>
                        <span
                          aria-hidden="true"
                          style={{
                            width: "1.125rem",
                            height: "1.125rem",
                            border: `1px solid ${selected ? "var(--accent-color)" : "var(--text-muted)"}`,
                            borderRadius: "999px",
                            background: selected ? "var(--accent-color)" : "transparent",
                            boxShadow: selected ? "0 0 0 3px var(--accent-bg)" : "none",
                          }}
                        />
                      </span>
                    </span>
                    <span
                      className="block mt-3"
                      style={{
                        color: selected ? "var(--accent-color)" : "var(--accent-text-soft)",
                        fontFamily: "var(--font-reflective)",
                        fontSize: "1.125rem",
                        fontStyle: "italic",
                        lineHeight: 1.1,
                      }}
                    >
                      {sublabel}
                    </span>
                  </button>
                );
              })}
            </div>
            {mark && !mark.resolvedAt && (
              <div className="mt-3">
                <div style={{ color: "var(--accent-color)", fontFamily: "var(--font-mono)", fontSize: "0.75rem", letterSpacing: "0.04em" }}>
                  {mark.mark === "change" ? "Looking closer" : "Leaving it open"}
                </div>
                <p className="mt-1" style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.875rem", lineHeight: 1.5 }}>
                  {mark.mark === "change"
                    ? `Marked ${safeDate(mark.markedAt, timeZone)}. This stays in view as something you want to understand.`
                    : "Marked. It can stay open without needing an answer."}
                </p>
              </div>
            )}
            {mark?.mark === "change" && !mark.resolvedAt && (
              <p className="mt-1" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", letterSpacing: "0.04em" }}>
                {sinceCount} {sinceCount === 1 ? "instance" : "instances"} since · {changeComparison} the month before
              </p>
            )}
            {canResolve && (
              <div className="mt-3">
                <p style={{ color: "var(--text-primary)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem" }}>
                  This hasn&apos;t appeared in {Number(recurrence?.daysSinceLast)} days.
                </p>
                <div className="mt-2 flex items-center gap-5">
                  <button type="button" onClick={() => onResolve(card.id, false)} disabled={isMarking} className="touch-target transition-opacity hover:opacity-80 disabled:opacity-50" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", letterSpacing: "0.04em" }} data-testid={`button-still-working-${card.id}`}>STILL WORKING ON IT</button>
                  <button type="button" onClick={() => onResolve(card.id, true)} disabled={isMarking} className="touch-target transition-opacity hover:opacity-80 disabled:opacity-50" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", letterSpacing: "0.04em" }} data-testid={`button-done-${card.id}`}>THIS ONE&apos;S DONE</button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div
            className="mt-3 pt-3"
            style={{ borderTop: "1px solid var(--border-hairline)" }}
            data-testid={`pattern-mark-unavailable-${card.id}`}
          >
            <p
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em" }}
            >
              PATTERN MARKING
            </p>
            <p className="mt-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.875rem", lineHeight: 1.5 }}>
              Pattern marking becomes available when this reaches 50% confidence. This is still an {Number(card.confidence) >= 50 ? "Observation" : "Early signal"}.
            </p>
          </div>
        )}

        {hasDetail && (
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-controls={`pattern-detail-${card.id}`}
            className="touch-target mt-3 transition-opacity hover:opacity-80"
            style={{ color: "var(--accent-text-soft)" }}
            data-testid={`button-expand-pattern-${card.id}`}
          >
            <span className="type-label">{expanded ? "Less ↑" : "More ↓"}</span>
          </button>
        )}

        {expanded && (
          <div
            id={`pattern-detail-${card.id}`}
            className="fade-in mt-4"
            style={{ borderTop: "1px solid var(--border-light)" }}
          >
            {safeString(card.narrative) && (
              <div className="py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <p
                  className="leading-[1.8]"
                  style={{
                    color: "var(--text-primary)",
                    fontFamily: "var(--font-reflective)",
                    fontSize: "0.9375rem",
                    fontWeight: 400,
                  }}
                  data-testid={`text-narrative-${card.id}`}
                >
                  <BoldText text={safeString(card.narrative)} />
                </p>
              </div>
            )}

            {safeArray(card.dataPoints).length > 0 && (
              <div className="py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <div
                  className="mb-3 type-label"
                  style={{
                    color: "var(--text-muted)",
                  }}
                >
                  EVIDENCE
                </div>
                <div className="space-y-2">
                  {safeArray(card.dataPoints).map((point, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <span
                        style={{
                          color: "var(--accent-color)",
                          fontFamily: "var(--font-display)",
                          fontSize: "0.75rem",
                          fontWeight: 500,
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className="leading-relaxed"
                        style={{
                          color: "var(--text-muted)",
                          fontFamily: "var(--font-reflective)",
                          fontSize: "0.9375rem",
                          fontWeight: 400,
                        }}
                      >
                        {formatTypographicQuotes(safeString(point))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {safeArray(card.contributingFactors).length > 0 && (
              <div className="py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <div className="flex flex-wrap gap-2">
                  {safeArray(card.contributingFactors).map((factor, i) => (
                    <span
                      key={i}
                      className="px-2.5 py-1 type-label"
                      style={{
                        color: "var(--text-primary)",
                        border: "1px solid var(--border-light)",
                        borderRadius: "6px",
                      }}
                    >
                      {formatTypographicQuotes(factor)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {safeString(card.recommendation) && (
              <div className="py-4" style={{ borderBottom: "1px solid var(--border-hairline)" }}>
                <div
                  className="mb-3 type-label"
                  style={{
                    color: "var(--text-muted)",
                  }}
                >
                  WHAT TO DO WITH THIS
                </div>
                <p
                  className="leading-relaxed"
                  style={{
                    color: "var(--text-primary)",
                    fontFamily: "var(--font-reflective)",
                    fontSize: "0.9375rem",
                    fontWeight: 400,
                  }}
                  data-testid={`text-recommendation-${card.id}`}
                >
                  <BoldText text={safeString(card.recommendation)} />
                </p>
              </div>
            )}

            <div className="py-3">
              <ActionChips />
            </div>

            <div
              className="py-3 flex items-center justify-end"
              style={{ borderTop: "1px solid var(--border-hairline)" }}
            >
              <span
                className="type-label"
                style={{
                  color: "var(--text-muted)",
                }}
              >
                {safeDate(card.generatedAt) ? `${safeDate(card.generatedAt)}` : ""}
              </span>
            </div>
          </div>
        )}
      </div>
    </MirrorCard>
  );
}

const PAGE_SIZE = 8;

export default function PatternsPage() {
  const queryClient = useQueryClient();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [otherVisibleCount, setOtherVisibleCount] = useState(PAGE_SIZE);
  const [selectedGroup, setSelectedGroup] = useState<PatternLens | null>(null);
  const { data: user } = useQuery<{ timezone?: string | null }>({ queryKey: ["/api/auth/me"] });
  const timeZone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { data: cards = [], isLoading, isError, error, refetch } = useQuery<EnrichedPatternCard[]>({
    queryKey: ["/api/patterns"],
    retry: 2,
    retryDelay: 1000,
    refetchInterval: query => {
       const data = query.state.data as EnrichedPatternCard[] | undefined;
      return data?.some(card => card.directAddressed === false) ? 1500 : false;
    },
  });

  const { data: status } = useQuery({
    queryKey: ["/api/status"],
    retry: 2,
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => apiRequest("PATCH", `/api/patterns/${id}/archive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/patterns"] });
    },
  });
  const markMutation = useMutation({
    mutationFn: ({ id, mark }: { id: number; mark: PatternMark["mark"] | null }) => mark
      ? apiRequest("PUT", `/api/patterns/${id}/mark`, { mark })
      : apiRequest("DELETE", `/api/patterns/${id}/mark`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/patterns"] }),
  });
  const resolveMutation = useMutation({
    mutationFn: ({ id, resolved }: { id: number; resolved: boolean }) =>
      apiRequest("POST", `/api/patterns/${id}/mark/resolve`, { resolved }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["/api/patterns"] }),
  });

  const analyzeMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/patterns/analyze", {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/patterns"] });
    },
  });

  const logCount = (status as any)?.count ?? 0;
  const hasEnoughData = logCount >= 7;
  const validCards = useMemo(
     () => (Array.isArray(cards) ? cards : []).filter(card => card && card.id != null),
    [cards],
  );
  const hasMarkablePattern = validCards.some(card => Number(card.confidence) >= PATTERN_MARK_CONFIDENCE && !card.patternMark?.resolvedAt);
  const patternGroups = useMemo(() => {
    return PATTERN_LENSES.flatMap(({ key, label }) => {
       const groupCards = validCards.filter(card => !card.patternMark?.resolvedAt && getPatternLens(card.patternType) === key);
      return groupCards.length > 0 ? [{ key, label, cards: groupCards }] : [];
    });
  }, [validCards]);
  const otherCards = useMemo(
    () => validCards.filter(card => !card.patternMark?.resolvedAt && getPatternLens(card.patternType) === null),
    [validCards],
  );
  const changedCards = useMemo(
    () => validCards.filter(card => Boolean(card.patternMark?.resolvedAt)),
    [validCards],
  );
  const activeGroup = patternGroups.find(group => group.key === selectedGroup)
    ?? patternGroups[0]
    ?? null;

  useEffect(() => {
    const nextGroup = activeGroup?.key ?? null;
    if (selectedGroup !== nextGroup) setSelectedGroup(nextGroup);
  }, [activeGroup?.key, selectedGroup]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeGroup?.key]);

  useEffect(() => {
    const requestedId = new URLSearchParams(window.location.search).get("pattern");
    if (!requestedId || validCards.length === 0) return;
    window.setTimeout(() => {
      document.querySelector(`[data-testid="pattern-card-${requestedId}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 0);
  }, [validCards]);

  const handlePatternTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    currentIndex: number,
  ) => {
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % patternGroups.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + patternGroups.length) % patternGroups.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = patternGroups.length - 1;
    if (nextIndex === null) return;

    event.preventDefault();
    setSelectedGroup(patternGroups[nextIndex].key);
    const tabs = event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    tabs?.[nextIndex]?.focus();
  };

  const handleRefresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["/api/patterns"] }),
      queryClient.invalidateQueries({ queryKey: ["/api/status"] }),
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] }),
    ]);
  }, [queryClient]);

  const { containerRef, isRefreshing, pullProgress, pullIndicatorStyle, handlers } = usePullToRefresh({
    onRefresh: handleRefresh,
  });

  return (
    <AtmosphericPage
      contained
      atmosphere={{ scrimVariant: "content", opacityScale: 0.4 }}
      contentClassName="h-full"
      data-testid="patterns-page"
    >
      <div
        className="flex flex-col h-full relative overflow-y-auto"
        ref={containerRef}
        onTouchStart={handlers.onTouchStart}
        onTouchMove={handlers.onTouchMove}
        onTouchEnd={handlers.onTouchEnd}
      >
        <div
          className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
          style={{
            borderBottom: "1px solid var(--border-medium)",
            background: "var(--atmosphere-panel-bg)",
            backdropFilter: "blur(14px)",
          }}
        >
        <div>
          <h1
            className="text-lg"
            style={{ fontFamily: "var(--font-display)", color: "var(--fg-raw)" }}
          >
            Patterns
          </h1>
          <p
            className="mt-0.5"
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: "var(--type-label-size)",
              fontWeight: 500,
              letterSpacing: "var(--type-label-tracking)",
              textTransform: "uppercase",
            }}
          >
            what Fractal sees in you
          </p>
        </div>

        {hasEnoughData && (
          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            data-testid="button-analyze-patterns"
            className="touch-target flex items-center gap-1.5 px-3 py-1.5 transition-all disabled:opacity-60"
            style={{
              color: "var(--text-muted)",
              fontFamily: "var(--font-display)",
              fontSize: "0.75rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              border: "1px solid var(--border-medium)",
              borderRadius: "8px",
            }}
          >
            <RefreshCw size={10} className={analyzeMutation.isPending ? "animate-spin" : ""} />
            {analyzeMutation.isPending ? "ANALYZING..." : "NEW ANALYSIS"}
          </button>
        )}
      </div>

        {hasEnoughData && validCards.length > 0 && !hasMarkablePattern && (
          <div
            className="mx-6 mt-5 px-4 py-3"
            style={{ border: "1px solid var(--border-light)", borderRadius: "8px", background: "var(--surface-1)" }}
            data-testid="pattern-marking-availability"
          >
            <p style={{ color: "var(--fg-raw)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", fontWeight: 600, letterSpacing: "0.08em" }}>
              PATTERN MARKING
            </p>
            <p className="mt-1.5" style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.875rem", lineHeight: 1.5 }}>
              You can choose how to hold a pattern once a card reaches 50% confidence. Your current cards are still observations.
            </p>
          </div>
        )}

      <div>
        <div style={pullIndicatorStyle} data-testid="pull-indicator-patterns">
          {isRefreshing ? (
            <div className="pull-spinner" />
          ) : pullProgress > 0 ? (
            <RefreshCw
              size={16}
              style={{
                color: "var(--accent-color)",
                opacity: pullProgress,
                transform: `rotate(${pullProgress * 360}deg)`,
                transition: "transform 0.1s ease",
              }}
            />
          ) : null}
        </div>
        {isError ? (
          <div className="flex flex-col items-center justify-center h-80 gap-5 px-8 text-center fade-in" data-testid="error-patterns">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center"
              style={{ border: "1px solid var(--border-medium)", background: "var(--surface-2)" }}
            >
              <RefreshCw size={18} style={{ color: "var(--text-muted)" }} />
            </div>
            <div
              className="text-2xl leading-tight"
              style={{ color: "var(--fg-raw)", fontFamily: "var(--font-display)" }}
            >
              Couldn't load patterns
            </div>
            <div
              className="leading-relaxed"
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
            >
              {error?.message?.includes("401") ? "Session expired. Try logging out and back in." : "Something went wrong loading your patterns."}
            </div>
            <button
              onClick={() => refetch()}
              data-testid="button-retry-patterns"
              className="touch-target mt-3 px-5 py-2.5"
              style={{
                color: "var(--accent-color)",
                border: "1px solid var(--accent-border)",
                borderRadius: "8px",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                background: "var(--accent-bg)",
              }}
            >
              TRY AGAIN
            </button>
          </div>
        ) : isLoading ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="flex gap-1.5">
              {[0, 1, 2, 3].map(i => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full"
                  style={{
                    backgroundColor: "var(--accent-text-soft)",
                    animation: `gentle-pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                  }}
                />
              ))}
            </div>
            <div style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
              Loading patterns...
            </div>
          </div>
        ) : !hasEnoughData ? (
          <div className="flex flex-col items-center justify-center h-80 gap-5 px-8 text-center fade-in" data-testid="empty-patterns">
            <div
              className="w-14 h-14 rounded-full breathe"
              style={{ border: "1px solid var(--accent-border)", background: "var(--accent-bg)" }}
            />
            <div
              className="text-3xl leading-tight"
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}
            >
              Nothing yet
            </div>
            <div
              className="leading-relaxed"
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
            >
              Patterns unlock at 7 entries. You have {logCount}.
            </div>
            <div className="mt-4 w-40">
              <div className="flex justify-between mb-1.5">
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  {logCount} LOGGED
                </span>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--type-label-size)",
                    letterSpacing: "var(--type-label-tracking)",
                    textTransform: "uppercase",
                  }}
                >
                    <span style={{ fontFamily: "var(--font-display)" }}>7</span> NEEDED
                </span>
              </div>
              <div className="h-1 relative overflow-hidden" style={{ background: "var(--surface-3)", borderRadius: "2px" }}>
                <div
                  className="absolute h-full"
                  style={{ width: `${(logCount / 7) * 100}%`, backgroundColor: "var(--accent-color)", opacity: 0.5, transition: "width 0.8s ease", borderRadius: "2px" }}
                />
              </div>
            </div>
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-80 gap-5 px-8 text-center fade-in" data-testid="watching-patterns">
            <div
              className="w-14 h-14 rounded-full breathe"
              style={{ border: "1px solid var(--border-hairline)", background: "var(--surface-1)" }}
            />
            <div
              className="text-3xl leading-tight"
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-display)" }}
            >
              Watching
            </div>
            <div
              className="leading-relaxed"
              style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}
            >
              Enough data to look. No clear patterns surfaced.
              <br />This can take a few analysis cycles.
            </div>
            <button
              onClick={() => analyzeMutation.mutate()}
              disabled={analyzeMutation.isPending}
              data-testid="button-run-analysis"
              className="touch-target mt-3 px-5 py-2.5 disabled:opacity-60"
              style={{
                color: "var(--text-muted)",
                border: "1px solid var(--border-medium)",
                borderRadius: "8px",
                fontFamily: "var(--font-display)",
                fontSize: "0.75rem",
                fontWeight: 500,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              {analyzeMutation.isPending ? "RUNNING ANALYSIS..." : "RUN DEEP ANALYSIS"}
            </button>
          </div>
        ) : (
          <div className="p-4 fade-in flex flex-col" style={{ gap: "20px" }}>
            {analyzeMutation.isPending && (
              <MirrorCard>
                <div className="flex items-center gap-3">
                  <RefreshCw size={12} className="animate-spin" style={{ color: "var(--accent-color)" }} />
                  <span style={{ color: "var(--text-muted)", fontFamily: "var(--font-reflective)", fontSize: "0.9375rem", fontWeight: 400 }}>
                    Running deep pattern analysis on {logCount} entries...
                  </span>
                </div>
              </MirrorCard>
            )}

            {activeGroup && (
              <>
                <div
                  className="flex gap-1 overflow-x-auto p-1"
                  style={{
                    background: "var(--surface-1)",
                    border: "1px solid var(--border-hairline)",
                    borderRadius: "12px",
                  }}
                  role="tablist"
                  aria-label="Pattern lenses"
                >
                  {patternGroups.map((group, index) => {
                    const isActive = group.key === activeGroup.key;
                    return (
                      <button
                        key={group.key}
                        id={`patterns-tab-${group.key}`}
                        type="button"
                        role="tab"
                        aria-controls="patterns-panel"
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        className="touch-target shrink-0 px-4 py-2 transition-colors"
                        style={{
                          color: isActive ? "var(--accent-color)" : "var(--text-muted)",
                          background: isActive ? "var(--accent-bg-strong)" : "transparent",
                          border: isActive ? "1px solid var(--accent-border)" : "1px solid transparent",
                          borderRadius: "9px",
                        }}
                        onClick={() => setSelectedGroup(group.key)}
                        onKeyDown={event => handlePatternTabKeyDown(event, index)}
                        data-testid={`patterns-tab-${group.key}`}
                      >
                        <span className="type-label">{group.label}</span>
                      </button>
                    );
                  })}
                </div>

                <section
                  id="patterns-panel"
                  role="tabpanel"
                  aria-labelledby={`patterns-tab-${activeGroup.key}`}
                  className="flex flex-col"
                  style={{ gap: "20px" }}
                  data-testid={`patterns-panel-${activeGroup.key}`}
                >
                  {activeGroup.cards.slice(0, visibleCount).map((card, index) => (
                    <PatternCardComponent
                      key={card.id}
                      card={card}
                      onArchive={id => archiveMutation.mutate(id)}
                      isArchiving={archiveMutation.isPending}
                      onMark={(id, mark) => markMutation.mutate({ id, mark })}
                      onResolve={(id, resolved) => resolveMutation.mutate({ id, resolved })}
                      isMarking={markMutation.isPending || resolveMutation.isPending}
                      timeZone={timeZone}
                      index={index}
                    />
                  ))}

                  {activeGroup.cards.length > visibleCount && (
                    <button
                      onClick={() => setVisibleCount(value => value + PAGE_SIZE)}
                      className="w-full py-3 text-center transition-opacity"
                      style={{
                        color: "var(--text-muted)",
                        fontFamily: "var(--font-display)",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        border: "1px solid var(--border-hairline)",
                        borderRadius: "10px",
                      }}
                      data-testid="button-load-more"
                    >
                      SHOW MORE ({activeGroup.cards.length - visibleCount} remaining)
                    </button>
                  )}
                </section>
              </>
            )}

            {otherCards.length > 0 && (
              <section
                className="flex flex-col"
                style={{ gap: "20px" }}
                aria-labelledby="patterns-other-heading"
                data-testid="patterns-group-other"
              >
                <div>
                  <div
                    id="patterns-other-heading"
                    className="type-label"
                    style={{ color: "var(--text-muted)" }}
                  >
                    OTHER OBSERVATIONS
                  </div>
                  <p
                    className="mt-1"
                    style={{
                      color: "var(--text-secondary)",
                      fontFamily: "var(--font-reflective)",
                      fontSize: "0.9375rem",
                    }}
                  >
                    Saved patterns that do not yet qualify for a Behavior, Timing, or Trends lens.
                  </p>
                </div>

                {otherCards.slice(0, otherVisibleCount).map((card, index) => (
                  <PatternCardComponent
                    key={card.id}
                    card={card}
                    onArchive={id => archiveMutation.mutate(id)}
                    isArchiving={archiveMutation.isPending}
                    onMark={(id, mark) => markMutation.mutate({ id, mark })}
                    onResolve={(id, resolved) => resolveMutation.mutate({ id, resolved })}
                    isMarking={markMutation.isPending || resolveMutation.isPending}
                    timeZone={timeZone}
                    index={index}
                  />
                ))}

                {otherCards.length > otherVisibleCount && (
                  <button
                    onClick={() => setOtherVisibleCount(value => value + PAGE_SIZE)}
                    className="w-full py-3 text-center transition-opacity"
                    style={{
                      color: "var(--text-muted)",
                      fontFamily: "var(--font-display)",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      letterSpacing: "0.04em",
                      textTransform: "uppercase",
                      border: "1px solid var(--border-hairline)",
                      borderRadius: "10px",
                    }}
                    data-testid="button-load-more-other"
                  >
                    SHOW MORE ({otherCards.length - otherVisibleCount} remaining)
                  </button>
                )}
              </section>
            )}
            {changedCards.length > 0 && (
              <section className="flex flex-col" style={{ gap: "12px" }} aria-labelledby="patterns-changed-heading" data-testid="patterns-group-changed">
                <div id="patterns-changed-heading" className="type-label" style={{ color: "var(--text-muted)" }}>CHANGED</div>
                {changedCards.map(card => (
                  <div key={card.id} className="py-3" style={{ borderTop: "1px solid var(--border-hairline)" }}>
                    <p style={{ color: "var(--fg-raw)", fontFamily: "var(--font-reflective)", fontSize: "1rem" }}>{formatTypographicQuotes(card.insight)}</p>
                    <p className="mt-1" style={{ color: "var(--text-muted)", fontFamily: "var(--font-mono)", fontSize: "0.6875rem", letterSpacing: "0.04em" }}>
                      Marked {safeDate(card.patternMark?.markedAt, timeZone)}. Last appeared {safeDate(card.recurrence?.lastAppearedAt, timeZone)}.
                    </p>
                  </div>
                ))}
              </section>
            )}
          </div>
        )}
        </div>
      </div>
    </AtmosphericPage>
  );
}
